import { Grid, theme as antdTheme } from "antd";
import type { GlobalToken } from "antd";

/**
 * Padding of the app's main content area, around every page. This file is the
 * single source of truth: the shell (`AppShellLayout`) applies it, and pages
 * with full-bleed blocks (backgrounds, edge-to-edge bars) cancel it with the
 * helpers below instead of hardcoding the values.
 *
 * Desktop: paddingLG top, paddingXL sides, PAGE_PADDING_BOTTOM bottom.
 * Below the sm breakpoint (compact): paddingSM all around.
 */

/** Design spec: bottom padding of every page. Not on the token scale. */
export const PAGE_PADDING_BOTTOM = 40;

export type PagePadding = Readonly<{
  top: number;
  right: number;
  bottom: number;
  left: number;
}>;

export const getPagePadding = (
  token: GlobalToken,
  compact: boolean
): PagePadding =>
  compact
    ? {
        top: token.paddingSM,
        right: token.paddingSM,
        bottom: token.paddingSM,
        left: token.paddingSM,
      }
    : {
        top: token.paddingLG,
        right: token.paddingXL,
        bottom: PAGE_PADDING_BOTTOM,
        left: token.paddingXL,
      };

/** `top right bottom left` in px. */
export const toCssBox = ({ top, right, bottom, left }: PagePadding) =>
  `${top}px ${right}px ${bottom}px ${left}px`;

/** The same box, negated (to cancel the page padding with a margin). */
export const toNegativeCssBox = ({ top, right, bottom, left }: PagePadding) =>
  `-${top}px -${right}px -${bottom}px -${left}px`;

/** Page padding for the current viewport (same breakpoint as the shell). */
export const usePagePadding = (): PagePadding => {
  const { token } = antdTheme.useToken();
  const breakpoint = Grid.useBreakpoint();
  return getPagePadding(token, breakpoint.sm === false);
};

/**
 * CSS for a full-bleed block: a negative margin cancels the page padding so
 * the block reaches the content area's edges. With `repad`, the page padding
 * is applied again inside it so its content stays where it was.
 */
export const pageBleedCss = (
  token: GlobalToken,
  { repad = false }: { repad?: boolean } = {}
) => {
  const regular = getPagePadding(token, false);
  const compact = getPagePadding(token, true);
  return `
    margin: ${toNegativeCssBox(regular)};
    ${repad ? `padding: ${toCssBox(regular)};` : ""}

    @media (width < ${token.screenSM}px) {
      margin: ${toNegativeCssBox(compact)};
      ${repad ? `padding: ${toCssBox(compact)};` : ""}
    }
  `;
};

/** CSS applying the page padding (e.g. inside a full-bleed background). */
export const pagePaddingCss = (token: GlobalToken) => `
  padding: ${toCssBox(getPagePadding(token, false))};

  @media (width < ${token.screenSM}px) {
    padding: ${toCssBox(getPagePadding(token, true))};
  }
`;
